// analytics.ts — frontend → /api/event helper.
//
// Design choices:
//   - sendBeacon when available (survives unload), fallback to fetch
//     keepalive. Either way, fire-and-forget — caller never awaits.
//   - Silently swallows network errors; analytics MUST NOT break UX.
//   - Event names are an enumerated union here so a typo at the call
//     site is a TS error.
//   - Props are shallow JSON; callers must avoid PII (free-text user
//     inputs). We don't enforce — just document.

import { getApiBase } from "./config";

export type AnalyticsEvent =
  | "signup_started"
  | "signup_role_selected"
  | "signup_prefs_continued"
  | "signup_prefs_skipped"
  | "signup_completed"
  | "workspace_opened"
  | "workspace_welcome_dismissed"
  | "workspace_prefs_edited"
  | "workspace_switcher_opened"
  | "workspace_created"
  | "tour_started"
  | "tour_step_advanced"
  | "tour_completed"
  | "tour_skipped"
  | "chat_welcome_dismissed"
  | "chat_sample_clicked"
  | "chat_message_sent"
  | "email_verify_resent";

export function track(name: AnalyticsEvent, props?: Record<string, unknown>): void {
  const body = JSON.stringify({ name, props: props ?? {} });
  const url = `${getApiBase()}/api/event`;
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      // sendBeacon returns false if the user agent declines to queue —
      // we fall through to fetch in that case.
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      // keepalive lets the request finish after the page tears down
      // (e.g. user clicks a link mid-tour).
      keepalive: true,
    }).catch(() => { /* ignore */ });
  } catch {
    // Last-ditch: never throw from a tracking call.
  }
}
